import React, { useState, useMemo, useCallback } from 'react';
import * as Popover from '@radix-ui/react-popover';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  isBefore,
  isAfter,
  startOfDay,
  eachDayOfInterval,
} from 'date-fns';
import { formatDate, getMonthNames, getWeekdayLabels } from '@/lib/localeFormatters';
import { useLocaleSettings } from '@/hooks/useLocaleSettings';
import { cn } from '@/lib/utils';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

export interface DateRangePickerProps {
  startDate?: Date | null;
  endDate?: Date | null;
  onChange?: (range: { start: Date | null; end: Date | null }) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  hint?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

function toDate(v: Date | string | null | undefined): Date | null {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * A date range picker that displays two calendar months side by side.
 *
 * - First click sets start date, second click sets end date
 * - If second date is before first, they are swapped automatically
 * - Locale-aware date formatting via the app's i18n/locale system
 * - Optional min/max date constraints
 * - Dark mode compatible using semantic Tailwind tokens
 * - Uses Radix UI Popover for accessible dropdown behavior
 */
export function DateRangePicker({
  startDate,
  endDate,
  onChange,
  placeholder,
  label,
  error,
  hint,
  disabled = false,
  minDate,
  maxDate,
  className,
}: DateRangePickerProps) {
  const safeStart = toDate(startDate);
  const safeEnd = toDate(endDate);
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState<Date>(() => safeStart ?? new Date());
  const [selectionStart, setSelectionStart] = useState<Date | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const inputId = React.useId();

  // Subscribe to locale store for reactive re-renders when settings change
  const localeSettings = useLocaleSettings();

  const hasError = Boolean(error);

  // Format the display value
  const displayValue = useMemo(() => {
    if (!safeStart) return '';
    const startStr = formatDate(safeStart);
    if (!safeEnd) return startStr;
    const endStr = formatDate(safeEnd);
    return `${startStr} \u2013 ${endStr}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeStart, safeEnd, localeSettings.dateFormat]);

  // Build calendar grids for current month and next month
  const buildCalendarDays = useCallback((monthDate: Date) => {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, []);

  const leftMonthDays = useMemo(() => buildCalendarDays(viewDate), [viewDate, buildCalendarDays]);
  const rightMonthDate = useMemo(() => addMonths(viewDate, 1), [viewDate]);
  const rightMonthDays = useMemo(() => buildCalendarDays(rightMonthDate), [rightMonthDate, buildCalendarDays]);

  // Weekday headers (Mon-first)
  const weekHeaders = useMemo(() => {
    const labels = getWeekdayLabels();
    return [...labels.slice(1), labels[0]];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeSettings.dateFormat]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const monthNames = useMemo(() => getMonthNames(), [localeSettings.dateFormat]);

  const isDateDisabled = useCallback(
    (date: Date): boolean => {
      const day = startOfDay(date);
      if (minDate && isBefore(day, startOfDay(minDate))) return true;
      if (maxDate && isAfter(day, startOfDay(maxDate))) return true;
      return false;
    },
    [minDate, maxDate],
  );

  const handleSelect = useCallback(
    (date: Date) => {
      if (isDateDisabled(date)) return;

      if (!selectionStart) {
        // First click: set start
        setSelectionStart(date);
      } else {
        // Second click: determine start and end, then commit
        let start = selectionStart;
        let end = date;
        if (isBefore(end, start)) {
          [start, end] = [end, start];
        }
        onChange?.({ start, end });
        setSelectionStart(null);
        setHoveredDate(null);
        setOpen(false);
      }
    },
    [selectionStart, onChange, isDateDisabled],
  );

  const handleClear = useCallback(() => {
    onChange?.({ start: null, end: null });
    setSelectionStart(null);
    setHoveredDate(null);
  }, [onChange]);

  const handleToday = useCallback(() => {
    const today = new Date();
    setViewDate(today);
    onChange?.({ start: today, end: today });
    setSelectionStart(null);
    setHoveredDate(null);
    setOpen(false);
  }, [onChange]);

  const handlePrevMonth = useCallback(() => {
    setViewDate((d) => subMonths(d, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewDate((d) => addMonths(d, 1));
  }, []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (disabled) return;
      if (nextOpen) {
        setViewDate(safeStart ?? new Date());
        setSelectionStart(null);
        setHoveredDate(null);
      }
      setOpen(nextOpen);
    },
    [disabled, safeStart],
  );

  // Determine range highlight state for a given day
  const getDayState = useCallback(
    (day: Date) => {
      // During active selection (first click made, hovering)
      if (selectionStart) {
        const isStart = isSameDay(day, selectionStart);
        if (isStart) return 'range-start';

        if (hoveredDate) {
          let rangeA = selectionStart;
          let rangeB = hoveredDate;
          if (isBefore(rangeB, rangeA)) [rangeA, rangeB] = [rangeB, rangeA];

          const isEnd = isSameDay(day, hoveredDate);
          if (isEnd) return 'range-end';

          if (
            isAfter(day, rangeA) &&
            isBefore(day, rangeB)
          ) {
            return 'in-range';
          }
        }
        return 'none';
      }

      // Committed range
      if (safeStart && safeEnd) {
        if (isSameDay(day, safeStart)) return 'range-start';
        if (isSameDay(day, safeEnd)) return 'range-end';
        if (
          isAfter(day, safeStart) &&
          isBefore(day, safeEnd)
        ) {
          return 'in-range';
        }
      }

      return 'none';
    },
    [selectionStart, hoveredDate, safeStart, safeEnd],
  );

  const renderMonth = (monthDate: Date, days: Date[]) => (
    <div>
      <div className="text-center mb-2">
        <span className="text-sm font-medium text-foreground select-none">
          {monthNames[monthDate.getMonth()]} {monthDate.getFullYear()}
        </span>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1">
        {weekHeaders.map((day) => (
          <div
            key={day}
            className="flex h-8 w-8 items-center justify-center text-xs font-medium text-muted-foreground select-none"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const inCurrentMonth = isSameMonth(day, monthDate);
          const today = isToday(day);
          const dateDisabled = isDateDisabled(day);
          const state = getDayState(day);
          const isEndpoint = state === 'range-start' || state === 'range-end';

          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={dateDisabled}
              onClick={() => handleSelect(day)}
              onMouseEnter={() => {
                if (selectionStart) setHoveredDate(day);
              }}
              className={cn(
                'flex h-8 w-8 items-center justify-center text-sm transition-colors',
                // Base state
                inCurrentMonth ? 'text-foreground' : 'text-muted-foreground',
                // Hover
                !isEndpoint && state !== 'in-range' && !dateDisabled && 'hover:bg-accent',
                // Range endpoints
                isEndpoint && 'bg-primary-500 text-white hover:bg-primary-500 rounded-md',
                // In-range background — use primary tint with guaranteed contrast
                state === 'in-range' && 'bg-primary-500/15 text-foreground hover:bg-primary-500/25',
                // Rounding for range
                !isEndpoint && state !== 'in-range' && 'rounded-md',
                // Today ring (only when not an endpoint)
                today && !isEndpoint && 'ring-1 ring-primary-500 rounded-md',
                // Disabled
                dateDisabled && 'opacity-40 cursor-not-allowed',
              )}
            >
              {format(day, 'd')}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-foreground mb-1"
        >
          {label}
        </label>
      )}

      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild disabled={disabled}>
          <button
            id={inputId}
            type="button"
            disabled={disabled}
            aria-invalid={hasError}
            aria-describedby={
              hasError
                ? `${inputId}-error`
                : hint
                  ? `${inputId}-hint`
                  : undefined
            }
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm font-sans',
              'ring-offset-background',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              hasError && 'border-error-500 focus-visible:ring-error-500',
              !safeStart && 'text-muted-foreground',
            )}
          >
            <span className="truncate">
              {displayValue || placeholder || 'Select date range'}
            </span>
            <Calendar className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            className={cn(
              'z-50 rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-md',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[side=bottom]:slide-in-from-top-2',
              'data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2',
              'data-[side=top]:slide-in-from-bottom-2',
            )}
          >
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <div className="flex-1" />

              <button
                type="button"
                onClick={handleNextMonth}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Two-month calendar grid */}
            <div className="flex gap-4">
              {renderMonth(viewDate, leftMonthDays)}
              {renderMonth(rightMonthDate, rightMonthDays)}
            </div>

            {/* Footer: Today + Clear */}
            <div className="mt-2 pt-2 border-t border-border flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={handleToday}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Today
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="text-sm text-muted-foreground hover:text-foreground font-medium"
              >
                Clear
              </button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {hint && !error && (
        <p
          id={`${inputId}-hint`}
          className="mt-1 text-sm text-muted-foreground"
        >
          {hint}
        </p>
      )}
      {error && (
        <p
          id={`${inputId}-error`}
          className="mt-1 text-sm text-error-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}
